<template>
  <div
    class="w-72 bg-white border border-gray-200 rounded-lg text-headline shadow-lg overflow-y-auto h-[55vh] 3xl:h-[60vh]"
  >
    <div class="p-2 space-y-0.5">
      <template v-for="(section, sIndex) in sections" :key="sIndex">
        <button
          type="button"
          data-cy="anchor-nav-section-button"
          class="w-full text-left px-3 py-2 font-semibold rounded-md hover:bg-gray-100"
          @click="navigateToElement(section.headerElement)"
        >
          {{ section.label }}
        </button>

        <div class="ml-3 border-l border-gray-200">
          <template
            v-for="(funder, fIndex) in section.funders"
            :key="`${sIndex}-${fIndex}`"
          >
            <button
              type="button"
              data-cy="anchor-nav-funder-button"
              class="w-full transform transition-colors duration-200 text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100"
              :class="[
                { 'bg-surface-user-blue/50': isFunderActive(sIndex, fIndex) },
              ]"
              @click="navigateToElement(funder.tableElement)"
            >
              {{ funder.name }}
            </button>

            <div class="ml-3 border-l border-gray-200">
              <button
                v-for="(product, pIndex) in funder.products"
                :key="`${sIndex}-${fIndex}-${pIndex}`"
                type="button"
                data-cy="anchor-nav-product-button"
                class="w-full transform transition-colors duration-200 text-left px-3 py-2 text-xs rounded-md flex items-center justify-between hover:bg-gray-100"
                :class="[
                  { 'bg-surface-user-blue/50': isFunderActive(sIndex, fIndex) },
                ]"
                @click="navigateToElement(funder.tableElement)"
              >
                <span class="truncate">{{ product.name }}</span>
                <div class="flex items-center shrink-0">
                  <span
                    v-if="product.statusColor"
                    data-cy="anchor-nav-status-dot"
                    class="relative w-2.5 h-2.5 rounded-full ring-1 ring-white"
                    :class="getColorClass(product.statusColor)"
                    :style="{
                      zIndex: (product.scorecardColors?.length ?? 0) + 1,
                    }"
                  />

                  <span
                    v-for="(color, cIndex) in product.scorecardColors"
                    :key="cIndex"
                    data-cy="anchor-nav-scorecard-dot"
                    class="relative w-2.5 h-2.5 rounded-full -ml-0.5 ring-1 ring-white"
                    :style="{
                      backgroundColor: color,
                      zIndex: (product.scorecardColors?.length ?? 0) - cIndex,
                    }"
                  />
                </div>
              </button>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
export interface AnchorNavigationProduct {
  name: string;
  statusColor?: UiStatusColor;
  scorecardColors?: string[];
}

export interface AnchorNavigationFunder {
  name: string;
  tableElement: HTMLElement | null;
  products: AnchorNavigationProduct[];
}

export interface AnchorNavigationSection {
  label: string;
  headerElement: HTMLElement | null;
  funders: AnchorNavigationFunder[];
}
</script>

<script setup lang="ts">
import { computed } from "vue";

import type { UiStatusColor } from "@/models/common";

const props = defineProps<{
  sections: AnchorNavigationSection[];
  scrollContainer: HTMLElement | null;
  activeIndices?: Set<number>;
}>();

// Pre-compute the flat index for each funder to avoid recalculating on every render
// Maps "sectionIndex-funderIndex" to the flat index in trackedItems
const funderIndexMap = computed(() => {
  const map = new Map<string, number>();
  let idx = 0;

  props.sections.forEach((section, sIndex) => {
    idx++; // header
    section.funders.forEach((_, fIndex) => {
      map.set(`${sIndex}-${fIndex}`, idx);
      idx++;
    });
  });

  return map;
});

// Check if a funder is active using the memoized index map
const isFunderActive = (sectionIndex: number, funderIndex: number) => {
  if (!props.activeIndices) {
    return false;
  }
  const idx = funderIndexMap.value.get(`${sectionIndex}-${funderIndex}`);
  return idx !== undefined && props.activeIndices.has(idx);
};

const getColorClass = (color: UiStatusColor) => ({
  "bg-green-500": color === "success",
  "bg-orange-500": color === "warning",
  "bg-red-500": color === "error",
});

const navigateToElement = (element: HTMLElement | null) => {
  if (!props.scrollContainer || !element) {
    return;
  }

  props.scrollContainer.scrollTo({
    top: element.offsetTop - 110,
    behavior: "smooth",
  });
};
</script>
